import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NSNavbar from '../../components/NSNavbar';
import NSFooter from '../../components/NSFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath, parentRouteHref } from '../../utils/brandRouting';
import { SUPPORT_EMAIL } from '../../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE, NUTRISCAN_RECOVERY_WINDOW, PUBLISHER } from '../../data/legalContent';

function useNSTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'nutriscan');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

const TOC = [
  { id: 'request',   title: '1. How to request deletion' },
  { id: 'verify',    title: '2. How we verify a request' },
  { id: 'window',    title: `3. The ${NUTRISCAN_RECOVERY_WINDOW} recovery period` },
  { id: 'after',     title: '4. What happens after that period' },
  { id: 'retained',  title: '5. What may be retained' },
  { id: 'billing',   title: '6. Subscriptions are separate' },
  { id: 'contact',   title: '7. Contact' },
];

/**
 * NutriScan account deletion. The website offers a mailto route only: there is
 * deliberately no web form, request database, or ticket workflow here. The
 * wording follows the approved T05 process exactly and makes no instant,
 * blanket, or response-time promise.
 */
export default function NSDeletion() {
  useNSTheme();
  const supportHref = parentRouteHref('/contact');
  useDocumentMeta(routeMetaFor('/apps/nutriscan/account-deletion'));

  return (
    <PageWrap testId="ns-deletion-page">
      <NSNavbar />

      <header className="ns-legal-header" style={{ paddingTop: 'calc(var(--nav-h) + 40px)' }}>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ justifyContent: 'center', color: 'var(--ns-lime)' }}>NutriScan · Legal</div>
          <h1 className="reveal d1">Account Deletion</h1>
          <p className="reveal d2" style={{ marginTop: 8 }} data-testid="ns-deletion-effective">
            Effective date: {EFFECTIVE_DATE} · {APP_LEGAL.nutriscan.version}
          </p>
        </div>
      </header>

      <div className="wrap">
        <div className="ns-summary-box reveal">
          <div className="title">The short version</div>
          <div className="ns-summary-grid">
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>Request deletion in the app while signed in, or by email from your NutriScan sign-in address.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>We verify that the request comes from the account owner.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>A verified request starts a {NUTRISCAN_RECOVERY_WINDOW} recovery period, and you can cancel during it.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>Deleting the account does not cancel a Google Play subscription.</span></div>
          </div>
        </div>
      </div>

      <section className="section" style={{ paddingTop: 64, background: 'var(--ns-bg)' }}>
        <div className="wrap">
          <div className="legal-layout">
            <nav className="legal-toc">
              <h6>Contents</h6>
              <ul>{TOC.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}</ul>
            </nav>

            <div className="legal-body" style={{ background: 'var(--ns-surf)', borderColor: 'var(--ns-border)' }}>
              <span className="legal-meta-tag" data-testid="ns-deletion-publisher">Published by {PUBLISHER} · Support: {SUPPORT_EMAIL}</span>

              <section id="request"><h2>1. How to request deletion</h2>
                <p>There are two ways to ask for your {APP_LEGAL.nutriscan.name} account to be deleted:</p>
                <ul>
                  <li><strong>In the app</strong> — use the account deletion option while signed in to NutriScan.</li>
                  <li><strong>By email</strong> — write to <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="ns-deletion-email">{SUPPORT_EMAIL}</a> from the Google account address you use to sign in to NutriScan, and say that you want your NutriScan account deleted.</li>
                </ul>
                <p>This website does not collect deletion requests through a form. Email is the web route.</p>
              </section>

              <section id="verify"><h2>2. How we verify a request</h2>
                <p>Before acting on a request, we verify that it comes from the owner of the account. Sending the request from the Google account address used for NutriScan helps us do that. We may ask you for further confirmation if we cannot match the request to an account.</p>
              </section>

              <section id="window"><h2>3. The {NUTRISCAN_RECOVERY_WINDOW} recovery period</h2>
                <p>Once a request is verified, the account enters a <strong>{NUTRISCAN_RECOVERY_WINDOW}</strong> recovery period. During that period you can cancel the request and keep the account — contact us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from the same address.</p>
              </section>

              <section id="after"><h2>4. What happens after that period</h2>
                <p>After the recovery period ends, the account and the personal information associated with it are deleted or de-identified, as applicable, except for the limited records described in the next section.</p>
              </section>

              <section id="retained"><h2>5. What may be retained</h2>
                <p>Some records may be kept where they are needed for:</p>
                <ul>
                  <li>legal obligations;</li>
                  <li>security;</li>
                  <li>preventing fraud or abuse;</li>
                  <li>administering a transaction or subscription; or</li>
                  <li>resolving a dispute.</li>
                </ul>
                <p>Those records are limited to what is needed for the purpose described.</p>
              </section>

              <section id="billing"><h2>6. Subscriptions are separate</h2>
                <div className="callout alert">
                  <i className="fas fa-triangle-exclamation"></i>
                  <span>Deleting your NutriScan account <strong>does not cancel a Google Play subscription</strong>. Manage or cancel a subscription in Google Play.</span>
                </div>
                <p>See the <Link to={brandPath('nutriscan', '/terms')}>Terms of Service</Link> for the subscription position and the <Link to={brandPath('nutriscan', '/privacy')}>Privacy Policy</Link> for the information NutriScan handles.</p>
              </section>

              <section id="contact"><h2>7. Contact</h2>
                <div className="callout info" style={{ background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)' }}>
                  <i className="fas fa-envelope" style={{ color: 'var(--ns-green)' }}></i>
                  <span><strong>Email:</strong> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a><br/>{PUBLISHER}</span>
                </div>
                <p style={{ marginTop: 16, fontSize: '0.85rem' }}>
                  See also the <Link to={brandPath('nutriscan', '/privacy')}>Privacy Policy</Link>, the{' '}
                  <Link to={brandPath('nutriscan', '/terms')}>Terms of Service</Link>, and the{' '}
                  <Link to={brandPath('nutriscan', '/faq')}>NutriScan FAQ</Link>. For anything else, use{' '}
                  {supportHref
                    ? <a href={supportHref}>TickleApps Support</a>
                    : <Link to="/contact">TickleApps Support</Link>}.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>

      <NSFooter />
    </PageWrap>
  );
}
